import React, { useContext } from 'react'
import styled from '@emotion/styled'
import { array, object, string } from 'prop-types'
import { makeImgResizerUrl } from '@jelly/utils'
import Media from 'react-media'
import { AdsenseContext } from '@jelly/components'
// Import Components
import { ArticleCard, EvergreenArticleCard } from './EvergreenArticleCard'

const DESKTOP_EVERGREEN_BREAKPOINT = `@media (min-width: 1025px) and (max-width: 1200px)`

// Styled Components via Emotion
const EvergreenArticleWrapper = styled.section`
	width: 100%;
	grid-column: span 2;
	-ms-grid-column-span: 2;

	${({ theme }) => theme.MQ.M} {
		margin-right: 14px;
	}

	@media (min-width: 1024px) {
		grid-column: span 1;
		-ms-grid-column-span: 1;
		-ms-grid-column: 1;
		-ms-grid-row: 2;

		@supports (display: grid) {
			align-content: start;
			display: grid;
		}
	}
`

const Title = styled.h2`
	margin: 0 0 1rem 0;
`

const EvergreenLayout = styled.div`
	display: block;

	${({ theme }) => theme.MQ.M} {
		display: grid;
		display: -ms-grid;
		grid-gap: 11px 24px;
		grid-template-columns: repeat(2, 1fr);
		-ms-grid-columns: 1fr 24px 1fr;
	}
`
const SingleThumbnail = styled.div`
	display: block;
	order: 1;
	width: 100%;
	-ms-grid-column: 1;
	-ms-grid-row: 1;

	${ArticleCard} {
		background: ${({ theme }) => theme.colors.gray[50]};
		padding: 10px 15px;
	}

	${DESKTOP_EVERGREEN_BREAKPOINT} {
		${ArticleCard} {
			padding: 15px;
		}
	}
`
const ArticleCardWrapper = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray[50]};
	cursor: pointer;
	display: block;
	padding: 11px 0;
	width: 100%;
`
const ArticleQuad = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 100%;
	${ArticleCardWrapper} :nth-of-type(1) {
		padding: 0 0 22px 0;
	}
	${({ theme }) => theme.MQ.M} {
		-ms-grid-column: 3;
		-ms-grid-row: 1;
		order: 2;

		@supports (display: grid) {
			display: grid;
		}
	}
`
const ArticleQuad2 = styled.div`
	${ArticleQuad};
	${ArticleCardWrapper} :nth-of-type(1) {
		${({ theme }) => theme.MQ.M} {
			${({ isSponsored = true }) => isSponsored && `padding: 0 0 22px 0;`}
		}
	}

	${({ theme }) => theme.MQ.M} {
		-ms-grid-column: 3;
		-ms-grid-row: 3;
		order: 5;

		@supports (display: grid) {
			display: grid;
		}
	}
`
const GroupThumbnail = styled.div`
	display: block;
	margin-right: 24px;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		-ms-grid-column: 1;
		-ms-grid-row: 3;
		order: 4;

		${ArticleCard} {
			background: ${({ theme }) => theme.colors.gray[50]};
			padding: 10px 15px;
		}
	}

	${DESKTOP_EVERGREEN_BREAKPOINT} {
		${ArticleCard} {
			padding: 15px;
		}
	}
`
const AdContainer = styled.div`
	margin: 12px auto 14px auto;

	${({ theme }) => theme.MQ.M} {
		grid-column: span 2;
		-ms-grid-column-span: 3; // Fix for IE11 😒
		-ms-grid-column: 1;
		-ms-grid-row: 2;
		order: 3;
	}
`
const ThumbnailCardWrapper = styled.div`
	height: 100%;
	overflow: hidden;

	&:hover {
		a {
			color: ${({ theme }) => theme.hubTemplate.articleTitleHover};
		}
	}
`
const Thumbnail = styled.img`
	cursor: pointer;
	display: block;
	width: 100%;
`
const ArticleCardInlineWrapper = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray[50]};
	display: inline-block;
	padding: 11px 0;
	width: 100%;
	${Thumbnail} {
		display: none;
	}

	${({ theme }) => theme.MQ.M} {
		border-bottom: none;
		padding: 0;
		width: calc(50% - 11px);
		${Thumbnail} {
			display: block;
			height: 120px;
		}
	}
`

EvergreenArticles.propTypes = {
	articles: array.isRequired,
	sponsor: object,
	title: string.isRequired,
}

/**
 * This is the Evergreen Article Section, curated hand-picked article data
 *
 * @param {Array} props.articles List of articles to display
 * @param {Object} props.sponsor Sponsored data, company name, logo, etc
 * @param {String} props.title Title of the section
 */
export function EvergreenArticles({ articles = [], sponsor, title }) {
	const isSponsored = !!sponsor
	const largeThumbnailArticle = articles[0]
	const thumbnailArticles = articles.slice(1, 3)
	const regularArticleSet1 = articles.slice(3, 7)
	const regularArticleSet2 = articles.slice(7)

	const { adsenseMediaQuery, hasAdsense, hasAdsenseMobile } = useContext(AdsenseContext)

	if (articles.length === 0) {
		return null
	}
	return (
		<EvergreenArticleWrapper>
			<Title>{title}</Title>
			<EvergreenLayout>
				<SingleThumbnail>
					<ThumbnailCardWrapper alt={largeThumbnailArticle.title} href={largeThumbnailArticle.path}>
						<Thumbnail
							alt={largeThumbnailArticle.title}
							src={makeImgResizerUrl({
								src: largeThumbnailArticle.thumbnail.path,
								method: 'h',
								h: '300-png',
							})}
						/>
						<EvergreenArticleCard sponsor={sponsor} {...largeThumbnailArticle} />
					</ThumbnailCardWrapper>
				</SingleThumbnail>
				{(hasAdsense || hasAdsenseMobile) && (
					<Media query={adsenseMediaQuery} defaultMatches={false}>
						{(matches) => matches && <AdContainer id="in-article-vca-1" />}
					</Media>
				)}
				<GroupThumbnail>
					{thumbnailArticles.map((article, i) => {
						const { canonical, title } = article
						const thumbnailSrc = makeImgResizerUrl({
							src: article.thumbnail && article.thumbnail.path,
							method: 'h',
							h: '300-png',
						})
						return (
							<ArticleCardInlineWrapper key={`${article.title}-${i}`}>
								<ThumbnailCardWrapper alt={title} href={canonical}>
									<Thumbnail alt={article.title} src={thumbnailSrc} />
									<EvergreenArticleCard sponsor={sponsor} {...article} />
								</ThumbnailCardWrapper>
							</ArticleCardInlineWrapper>
						)
					})}
				</GroupThumbnail>
				<ArticleQuad>
					{regularArticleSet1.map((article, i) => {
						return (
							<ArticleCardWrapper key={`${article.title}-${i}`}>
								<EvergreenArticleCard sponsor={sponsor} {...article} />
							</ArticleCardWrapper>
						)
					})}
				</ArticleQuad>
				<ArticleQuad2 isSponsored={isSponsored}>
					{regularArticleSet2.map((article, i) => {
						return (
							<ArticleCardWrapper key={`${article.title}-${i}`}>
								<EvergreenArticleCard sponsor={sponsor} {...article} />
							</ArticleCardWrapper>
						)
					})}
				</ArticleQuad2>
			</EvergreenLayout>
		</EvergreenArticleWrapper>
	)
}
